import React from 'react';
import Image from 'next/image';
import { ButtonPrimary } from '../../atoms/Button/ButtonPrimary';
import { Label, Paragraph } from '../../typography';
import { ProductCardSpecs, ProductCardSpecsProps } from '../../molecules/ProductCardSpecs';
import { Link } from '../../atoms/Link/link';
import Badge, { BadgeColor } from '../../atoms/Badge/badge';
import { BadgeText } from '../../../types/common';

const FALLBACK_ASSET_SRC = '/assets/default_product_asset.gif';

type ProductCardProps = {
  title: string;
  subtitle: string;
  imageSrc: string;
  href: string;
  specs: ProductCardSpecsProps;
  onClick: () => void;
  ctaText: string;
  badgeSpecs?: Array<{ badgeColor: BadgeColor; badgeText: BadgeText }>;
  onCtaClick?: (e?: React.MouseEvent) => void;
};

export default function ProductCard({
  title,
  subtitle,
  imageSrc,
  href,
  onClick,
  specs,
  ctaText,
  badgeSpecs,
  onCtaClick,
}: ProductCardProps) {
  return (
    <div className="max-w-[364px] text-left">
      <Link href={href} onClick={onClick} headless>
        <>
          <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-neutral-100">
            {badgeSpecs && badgeSpecs.length > 0 && (
              <div className="absolute top-2.5 left-2.5 z-1 flex gap-2">
                {badgeSpecs.map((badge) => (
                  <Badge key={badge.badgeText} color={badge.badgeColor}>
                    {badge.badgeText}
                  </Badge>
                ))}
              </div>
            )}
            <Image
              alt={title}
              src={imageSrc || FALLBACK_ASSET_SRC}
              width={364}
              height={364}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="mt-4">
            <ProductCardSpecs {...specs} />
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <Label className="overflow-hidden text-ellipsis whitespace-nowrap">{title}</Label>
            <Paragraph className="overflow-hidden text-ellipsis whitespace-nowrap">
              {subtitle ? `By ${subtitle}` : 'Made with Makeship'}
            </Paragraph>
          </div>
        </>
      </Link>
      <div className="mt-6">
        <ButtonPrimary {...(onCtaClick ? { onClick: onCtaClick, href } : { href })} className="w-full">
          {ctaText}
          <span className="sr-only">, {title}</span>
        </ButtonPrimary>
      </div>
    </div>
  );
}
